import { ref, type App } from 'vue'

type Registro = Record<string, unknown>
type ChaveSeletor = string | number
type ContratoSelecaoNaoVazia = {
  selecionados: ItemSeletorImaginii[]
}
type StoreLegada = Readonly<{
  state: Registro
  commit: (tipo: string, valor: unknown) => void
}>

export type ItemSeletorImaginii = Readonly<{
  chave: ChaveSeletor
  valor: string
  legenda?: string
  badge?: string
  desabilitado?: boolean
}>

export type ListaItensSeletorImaginii = Readonly<{
  carregados: ItemSeletorImaginii[]
  selecionados: ItemSeletorImaginii[]
}>

export type ConfiguracaoAdaptadorSeletorLegado = Readonly<{
  chave?: string
  valor?: string
  legenda?: string
  selecaoMultipla?: boolean
  somenteLeitura?: boolean
  permiteLimpar?: boolean
  opcaoNula?: string
  valorOpcaoNula?: unknown
}>

export type FonteSeletorLegado = Readonly<{
  loaded?: unknown
  selected?: unknown
  itens?: unknown
  selecao?: unknown
}>

const ehRegistro = (valor: unknown): valor is Registro => (
  typeof valor === 'object' && valor !== null && !Array.isArray(valor)
)

const contratosSelecaoNaoVazia = new WeakMap<object, ContratoSelecaoNaoVazia>()
const CHAVE_INTERNA_OPCAO_NULA = '__pontotel_opcao_nula__'
const revisaoSelecaoNaoVazia = ref(0)

const paraLista = (valor: unknown): unknown[] => {
  if (valor === null || valor === undefined) return []
  return Array.isArray(valor) ? valor : [valor]
}

const normalizarChave = (valor: unknown): ChaveSeletor | undefined => {
  if (typeof valor === 'string') return valor.trim() ? valor : undefined
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : undefined
  if (typeof valor === 'boolean') return String(valor)
  return undefined
}

const obterCampo = (valor: unknown, campo: string): unknown => (
  ehRegistro(valor) ? valor[campo] : undefined
)

const obterChave = (
  opcaoOuChave: unknown,
  campo: string,
): ChaveSeletor | undefined => normalizarChave(
  ehRegistro(opcaoOuChave) ? obterCampo(opcaoOuChave, campo) : opcaoOuChave,
)

const criarItem = (
  opcao: unknown,
  configuracao: ConfiguracaoAdaptadorSeletorLegado,
): ItemSeletorImaginii | null => {
  const chave = obterChave(opcao, configuracao.chave ?? 'id')
  const valorOriginal = configuracao.valor
    ? (obterCampo(opcao, configuracao.valor) ?? obterCampo(opcao, 'name'))
    : obterCampo(opcao, 'name')
  if (chave === undefined || valorOriginal === null || valorOriginal === undefined) {
    return null
  }
  const valor = String(valorOriginal).trim()
  if (!valor) return null

  const legendaOriginal = configuracao.legenda
    ? obterCampo(opcao, configuracao.legenda)
    : undefined
  const legenda = legendaOriginal === null || legendaOriginal === undefined
    ? ''
    : String(legendaOriginal).trim()

  return legenda ? { chave, valor, legenda } : { chave, valor }
}

const deduplicarItens = (
  itens: readonly ItemSeletorImaginii[],
): ItemSeletorImaginii[] => {
  const vistos = new Set<string>()
  return itens.filter(({ chave }) => {
    const identificador = `${typeof chave}:${String(chave)}`
    if (vistos.has(identificador)) return false
    vistos.add(identificador)
    return true
  })
}

const normalizarFonte = (fonte: FonteSeletorLegado | readonly unknown[]): {
  itens: unknown[]
  selecao: unknown[]
} => {
  if (Array.isArray(fonte)) return { itens: [...fonte], selecao: [] }
  const estado = fonte as FonteSeletorLegado
  return {
    itens: paraLista(estado.itens ?? estado.loaded),
    selecao: paraLista(estado.selecao ?? estado.selected),
  }
}

const criarOpcaoNulaLegada = (
  configuracao: ConfiguracaoAdaptadorSeletorLegado,
): Registro => ({
  [configuracao.chave ?? 'id']: configuracao.valorOpcaoNula ?? null,
  [configuracao.valor ?? 'name']: configuracao.opcaoNula,
})

const correspondeAOpcaoNula = (
  selecao: unknown,
  configuracao: ConfiguracaoAdaptadorSeletorLegado,
): boolean => configuracao.opcaoNula !== undefined
  && ehRegistro(selecao)
  && obterCampo(selecao, configuracao.chave ?? 'id')
    === (configuracao.valorOpcaoNula ?? null)

export const criarListaItensDoLegado = (
  fonte: FonteSeletorLegado | readonly unknown[],
  configuracao: ConfiguracaoAdaptadorSeletorLegado = {},
): ListaItensSeletorImaginii => {
  // Recria o modelValue após uma tentativa rejeitada de limpar. O PontotelSeletor
  // mantém seleção local e só a sincroniza quando a lista recebida muda.
  void revisaoSelecaoNaoVazia.value
  const { itens, selecao } = normalizarFonte(fonte)
  const itensCarregados = deduplicarItens(
    itens.map((opcao) => criarItem(opcao, configuracao)).filter(
      (item): item is ItemSeletorImaginii => item !== null,
    ),
  )
  const opcaoNula = configuracao.opcaoNula === undefined
    ? []
    : [{ chave: CHAVE_INTERNA_OPCAO_NULA, valor: configuracao.opcaoNula }]
  const carregados = [...opcaoNula, ...itensCarregados]
  const opcoesPorChave = new Map<string, unknown>()
  for (const opcao of itens) {
    const chave = obterChave(opcao, configuracao.chave ?? 'id')
    if (chave !== undefined) opcoesPorChave.set(String(chave), opcao)
  }
  const selecionados = deduplicarItens(
    selecao.map((selecionado) => {
      if (correspondeAOpcaoNula(selecionado, configuracao)) {
        return opcaoNula[0] ?? null
      }
      const chave = obterChave(selecionado, configuracao.chave ?? 'id')
      if (chave === undefined) return null
      const opcao = opcoesPorChave.get(String(chave))
        ?? (ehRegistro(selecionado) ? selecionado : undefined)
      return opcao ? criarItem(opcao, configuracao) : null
    }).filter((item): item is ItemSeletorImaginii => item !== null),
  ).slice(0, configuracao.selecaoMultipla ? undefined : 1)

  if (configuracao.permiteLimpar === false) {
    contratosSelecaoNaoVazia.set(itens, { selecionados })
  } else {
    contratosSelecaoNaoVazia.delete(itens)
  }

  return {
    carregados: configuracao.somenteLeitura ? selecionados : carregados,
    selecionados,
  }
}

export const extrairSelecaoDoLegado = (
  estado: ListaItensSeletorImaginii,
  opcoes: readonly unknown[],
  configuracao: ConfiguracaoAdaptadorSeletorLegado = {},
): unknown | readonly unknown[] | null => {
  const contrato = contratosSelecaoNaoVazia.get(opcoes)
  const rejeitouSelecaoVazia = estado.selecionados.length === 0
    && contrato?.selecionados.length
  const itensSelecionados = rejeitouSelecaoVazia
    ? contrato.selecionados
    : estado.selecionados
  if (rejeitouSelecaoVazia) revisaoSelecaoNaoVazia.value += 1
  if (estado.selecionados.length > 0 && contrato) {
    contrato.selecionados = [...estado.selecionados]
  }
  const opcoesPorChave = new Map<string, unknown>()
  for (const opcao of opcoes) {
    const chave = obterChave(opcao, configuracao.chave ?? 'id')
    if (chave !== undefined) opcoesPorChave.set(String(chave), opcao)
  }
  const vistos = new Set<string>()
  const selecionados = itensSelecionados.flatMap(({ chave }) => {
    const identificador = String(chave)
    if (vistos.has(identificador)) return []
    vistos.add(identificador)
    if (identificador === CHAVE_INTERNA_OPCAO_NULA
      && configuracao.opcaoNula !== undefined) {
      return [criarOpcaoNulaLegada(configuracao)]
    }
    const opcao = opcoesPorChave.get(identificador)
    return opcao === undefined ? [] : [opcao]
  })

  return configuracao.selecaoMultipla ? selecionados : selecionados[0] ?? null
}

export const extrairEstadoDoLegado = (
  estado: ListaItensSeletorImaginii,
  opcoes: readonly unknown[],
  configuracao: ConfiguracaoAdaptadorSeletorLegado = {},
): Readonly<{ loaded: readonly unknown[], selected: unknown }> => ({
  loaded: opcoes,
  selected: extrairSelecaoDoLegado(estado, opcoes, configuracao),
})

export const extrairIdsSelecionados = (
  estado: ListaItensSeletorImaginii,
): ChaveSeletor[] => estado.selecionados.map(({ chave }) => chave)

export const obterEstadoDaStoreLegada = (
  store: StoreLegada,
  caminho: string,
): unknown => caminho.split('/').reduce<unknown>((estado, parte) => (
  ehRegistro(estado) ? estado[parte] : undefined
), store.state)

export const atualizarEstadoDaStoreLegada = (
  store: StoreLegada,
  caminho: string,
  estado: unknown,
): void => store.commit(caminho, estado)

const legarOpcaoCatalogo = (opcao: unknown): unknown => {
  if (!ehRegistro(opcao)) return opcao
  return {
    ...opcao,
    ...('nome' in opcao ? { name: opcao.nome } : {}),
    ...('codigo' in opcao ? { code: opcao.codigo } : {}),
    ...('locaisDeTrabalhoIds' in opcao && Array.isArray(opcao.locaisDeTrabalhoIds)
      ? { companies: opcao.locaisDeTrabalhoIds.map((id) => ({ id })) }
      : {}),
    ...('descricao' in opcao ? { description: opcao.descricao } : {}),
    ...('tipo' in opcao ? { kind: opcao.tipo } : {}),

  }
}

export const obterSelecaoParaCatalogo = (fonte: unknown): unknown => {
  const selecao = ehRegistro(fonte) && 'selected' in fonte
    ? fonte.selected
    : fonte
  return selecao ?? null
}

export const criarEstadoLegadoDoCatalogo = (
  estadoAnterior: unknown,
  selecao: unknown,
): Readonly<{ loaded: readonly unknown[], selected: unknown }> => {
  const carregados = ehRegistro(estadoAnterior) && Array.isArray(estadoAnterior.loaded)
    ? estadoAnterior.loaded
    : []
  const selecionado = Array.isArray(selecao)
    ? selecao.map(legarOpcaoCatalogo)
    : (selecao === null ? null : legarOpcaoCatalogo(selecao))
  return { loaded: carregados, selected: selecionado }
}

export const ADAPTADOR_SELETOR_LEGADO = Object.freeze({
  criarListaItens: criarListaItensDoLegado,
  extrairSelecao: extrairSelecaoDoLegado,
  extrairEstado: extrairEstadoDoLegado,
  extrairIds: extrairIdsSelecionados,
  obterEstado: obterEstadoDaStoreLegada,
  atualizarEstado: atualizarEstadoDaStoreLegada,
  selecaoCatalogo: obterSelecaoParaCatalogo,
  estadoCatalogo: criarEstadoLegadoDoCatalogo,
})

export type AdaptadorSeletorLegado = typeof ADAPTADOR_SELETOR_LEGADO

export const instalarAdaptadorSeletorLegado = (app: App): void => {
  app.config.globalProperties.$seletorLegado = ADAPTADOR_SELETOR_LEGADO
}
